import { useCallback, useContext, useMemo } from 'react';
import type { CellContext } from '@tanstack/react-table';
import styled from '@emotion/styled';
import { TranscriptType } from '@trinity/chat';
import type { ProjectClips } from '@trinity/chat/types';
import { NavigationContext } from '../../containers/Chat/RoutingProvider';
import { Tooltip } from '../presentation/Tooltip';
import { getLocationFor, trunc } from '../../utils/index';

type Props = CellContext<ProjectClips.Item, unknown>;

export const SourceCell = ({ row }: Props) => {
  const navigate = useContext(NavigationContext);

  const location = useMemo(() => {
    const transcriptId = row.original.transcript.id;
    const context = row.original.transcript.context;
    const contentStart = row.original.content[0]?.range.start;
    const position = contentStart ?? row.original.range.start;

    if (context.type === TranscriptType.VideoCall) {
      return getLocationFor.call.transcript({
        callId: context.callId,
        position,
        slug: context.projectId.toString(),
      });
    } else if (context.type === TranscriptType.WorkspaceFile) {
      return getLocationFor.workspaces.transcript({
        position,
        transcriptId,
      });
    }
    return null;
  }, [row.original.content, row.original.range, row.original.transcript]);

  const fullPath = useMemo(() => {
    if (location == null) return undefined;
    if (typeof location === 'string') return location;
    return [location.pathname, location.search].filter(Boolean).join('');
  }, [location]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!navigate || !fullPath) return;
      e.preventDefault();
      navigate(fullPath);
    },
    [navigate, fullPath]
  );

  return (
    <Tooltip title={row.original.transcript.name}>
      <Link href={fullPath} onClick={handleClick}>
        {trunc(row.original.transcript.name, 35)}
      </Link>
    </Tooltip>
  );
};

SourceCell.displayName = 'ProjectClips.SourceCell';

const Link = styled.a`
  color: var(--hyperlink);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
